import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GraduationCap, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { GameState } from "@/types/poker";
import { getCoachMessages, LESSON_CONCEPTS } from "../coach-messages";
import type { CoachMessage } from "../coach-messages";
import { lt } from "../localize";

// ---------------------------------------------------------------------------
// Position helper — derive named position from dealerIndex + player seat
// ---------------------------------------------------------------------------

function getPositionName(
  playerIndex: number,
  dealerIndex: number,
  totalPlayers: number,
): string {
  if (totalPlayers <= 1) return "";
  // seats relative to dealer: 0 = BTN, 1 = SB, 2 = BB, ...
  const offset = (playerIndex - dealerIndex + totalPlayers) % totalPlayers;

  if (totalPlayers === 2) {
    // Heads-up: dealer is SB, other is BB
    return offset === 0 ? "SB" : "BB";
  }

  if (offset === 0) return "BTN";
  if (offset === 1) return "SB";
  if (offset === 2) return "BB";

  // Remaining seats: UTG, UTG+1, ..., HJ, CO
  const remaining = totalPlayers - 3; // seats after BB
  const seatFromBB = offset - 3; // 0-indexed from UTG

  if (remaining <= 1) return "UTG";
  if (remaining === 2) return seatFromBB === 0 ? "UTG" : "CO";
  if (remaining === 3) {
    if (seatFromBB === 0) return "UTG";
    if (seatFromBB === 1) return "MP";
    return "CO";
  }
  // 4+ remaining
  if (seatFromBB === 0) return "UTG";
  if (seatFromBB === 1) return "UTG+1";
  if (seatFromBB === remaining - 2) return "HJ";
  if (seatFromBB === remaining - 1) return "CO";
  return "MP";
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CoachOverlayProps {
  lessonId: string;
  gameState: GameState;
  playerId: string;
  onDismiss: () => void;
  handsPlayed: number;
  maxHands: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CoachOverlay = ({
  lessonId,
  gameState,
  playerId,
  onDismiss,
  handsPlayed,
  maxHands,
}: CoachOverlayProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [visibleMessage, setVisibleMessage] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const shownInHandRef = useRef<string | null>(null);
  const autoDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track hand number changes to reset per-hand state
  // We detect a new hand when phase transitions to "preflop" after being idle/showdown
  const prevPhaseRef = useRef(gameState.phase);

  const isComplete = handsPlayed >= maxHands;

  // Derive player context
  const playerCtx = useMemo(() => {
    const playerIdx = gameState.players.findIndex((p) => p.id === playerId);
    const position =
      playerIdx >= 0
        ? getPositionName(
            playerIdx,
            gameState.dealerIndex,
            gameState.players.length,
          )
        : undefined;

    const isPreflop = gameState.phase === "preflop";

    return {
      position,
      phase: gameState.phase,
      isPreflop,
    };
  }, [gameState, playerId]);

  // Determine the trigger based on game state
  const currentTrigger = useMemo((): CoachMessage["trigger"] | null => {
    const isMyTurn =
      gameState.actionIndex != null &&
      gameState.actionIndex >= 0 &&
      gameState.players[gameState.actionIndex]?.id === playerId;

    if (gameState.phase === "showdown" || gameState.phase === "idle") {
      return "hand_complete";
    }
    if (isMyTurn) return "your_turn";
    if (gameState.phase === "preflop") return "preflop";
    if (["flop", "turn", "river"].includes(gameState.phase)) return "postflop";
    return null;
  }, [gameState, playerId]);

  // Reset per-hand tracking when a new hand starts
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = gameState.phase;

    if (
      gameState.phase === "preflop" &&
      (prev === "idle" || prev === "showdown")
    ) {
      shownInHandRef.current = null;
      setDismissed(false);
    }
  }, [gameState.phase]);

  // Pick and show a message
  useEffect(() => {
    if (isComplete || dismissed) return;
    if (!currentTrigger) return;
    if (shownInHandRef.current) return; // Already showed a message this hand

    const messages = getCoachMessages(lessonId);
    const eligible = messages.filter((m) => {
      if (m.trigger !== currentTrigger) return false;
      if (m.condition && !m.condition(playerCtx)) return false;
      return true;
    });

    if (eligible.length === 0) return;

    // Pick a random eligible message
    const picked = eligible[Math.floor(Math.random() * eligible.length)];
    const text = lt(picked.message, lang);

    shownInHandRef.current = text;
    setVisibleMessage(text);

    // Auto-dismiss after 5 seconds
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
    }
    autoDismissTimerRef.current = setTimeout(() => {
      setVisibleMessage(null);
    }, 5000);

    return () => {
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
      }
    };
  }, [currentTrigger, playerCtx, lessonId, lang, dismissed, isComplete]);

  const handleDismissMessage = useCallback(() => {
    setVisibleMessage(null);
    setDismissed(true);
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
    }
  }, []);

  // Practice complete overlay
  if (isComplete) {
    const concept = LESSON_CONCEPTS[lessonId];
    const conceptText = concept ? lt(concept, lang) : lessonId;

    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="rounded-xl bg-card p-8 text-center shadow-2xl max-w-sm mx-4 space-y-4">
          <div className="rounded-full bg-primary/20 p-4 mx-auto w-fit">
            <CheckCircle2 className="h-12 w-12 text-primary" />
          </div>
          <h2 className="text-2xl font-bold">{t("coach_complete")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("coach_complete_desc", {
              concept: conceptText,
              count: maxHands,
            })}
          </p>
          <Button
            className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
            onClick={onDismiss}
          >
            {t("poker_back_lobby")}
          </Button>
        </div>
      </div>
    );
  }

  if (!visibleMessage) return null;

  return (
    <div className="fixed left-0 right-0 top-14 z-40 px-3 pointer-events-none">
      <div className="mx-auto max-w-5xl pointer-events-auto">
        <div className="glass flex items-start gap-3 px-4 py-3 shadow-lg animate-in fade-in slide-in-from-top-2 duration-300">
          {/* Hand counter badge */}
          <span className="shrink-0 rounded-full bg-primary/20 px-2.5 py-0.5 text-xs font-bold text-primary tabular-nums">
            {t("coach_hand_counter", {
              current: handsPlayed,
              total: maxHands,
            })}
          </span>

          {/* Icon */}
          <div className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-primary/20">
            <GraduationCap className="h-4 w-4 text-primary" />
          </div>

          {/* Message */}
          <p className="flex-1 text-sm text-foreground/90 leading-snug pt-0.5">
            {visibleMessage}
          </p>

          {/* Dismiss */}
          <button
            onClick={handleDismissMessage}
            className="shrink-0 rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
